import type { BadgeTone } from '@/shared/components/ui/Badge';
import type { AccessControl, Note } from './types';

export interface AccessMeta {
  value: AccessControl;
  label: string;
  tone: BadgeTone;
  description: string;
}

/** The three real backend sharing scopes (notepadSchema.access_control enum). */
export const ACCESS_OPTIONS: AccessMeta[] = [
  { value: 'private', label: 'Private', tone: 'neutral', description: 'Only you can see this note.' },
  { value: 'user', label: 'Specific Users', tone: 'info', description: 'Shared with the users you choose.' },
  { value: 'department', label: 'Department', tone: 'primary', description: 'Shared with whole departments.' },
];

export function accessMeta(value: AccessControl): AccessMeta {
  return ACCESS_OPTIONS.find((o) => o.value === value) ?? ACCESS_OPTIONS[0];
}

/** Short human label for the list "Sharing" column. */
export function sharingSummary(note: Note): string {
  if (note.access_control === 'user') {
    const n = note.shared_with_users?.length ?? 0;
    return n === 1 ? 'Shared · 1 user' : `Shared · ${n} users`;
  }
  if (note.access_control === 'department') {
    const depts = note.shared_with_depts ?? [];
    if (depts.length === 0) return 'Department';
    if (depts.length <= 2) return depts.map(prettyDept).join(', ');
    return `${depts.length} departments`;
  }
  return 'Private';
}

/** sales → Sales, digital_marketing → Digital Marketing. */
export function prettyDept(dept: string): string {
  return dept
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

export type NoteSort = 'updated_desc' | 'updated_asc' | 'title_asc' | 'created_desc';

export const SORT_OPTIONS: { value: NoteSort; label: string }[] = [
  { value: 'updated_desc', label: 'Last updated' },
  { value: 'updated_asc', label: 'Oldest updated' },
  { value: 'created_desc', label: 'Newest created' },
  { value: 'title_asc', label: 'Title (A–Z)' },
];

/**
 * Sidebar "smart views" — derived entirely from REAL backend metadata
 * (ownership + access_control + recency). No fabricated folders/favorites/trash.
 */
export type NoteView = 'all' | 'mine' | 'shared' | 'recent' | 'private' | 'users' | 'department';

export const NOTE_VIEWS: { value: NoteView; label: string; group: 'library' | 'access' }[] = [
  { value: 'all', label: 'All Notes', group: 'library' },
  { value: 'mine', label: 'My Notes', group: 'library' },
  { value: 'shared', label: 'Shared with Me', group: 'library' },
  { value: 'recent', label: 'Recent', group: 'library' },
  { value: 'private', label: 'Private', group: 'access' },
  { value: 'users', label: 'Shared with Users', group: 'access' },
  { value: 'department', label: 'Department', group: 'access' },
];

/** A short plain-text preview of a note's body for list cards. */
export function notePreview(text?: string, max = 90): string {
  const t = (text ?? '').replace(/\s+/g, ' ').trim();
  if (!t) return 'No additional text';
  return t.length > max ? `${t.slice(0, max)}…` : t;
}
